import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { endOfWeek, format, startOfWeek } from "date-fns";
import { toast } from "react-toastify";
import ManagerNavbar from "../Layout/ManagerNavbar";
import API_BASE_URL from "../config";
import AdminTimesheetEvaluationModal from "../dashboard/AdminTimesheetEvaluationModal";

const ManagerEvaluation = () => {
  const token = localStorage.getItem("token");
  const managerId = localStorage.getItem("employeeId");

  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [evaluationData, setEvaluationData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("");
  const [selectedYear, setSelectedYear] = useState(format(new Date(), "yyyy"));
  const [filterOptions, setFilterOptions] = useState([]);
  const fetchIdRef = React.useRef(0);

  const getCurrentMonth = () => format(new Date(), "MMMM yyyy");
  const getCurrentWeek = () => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    return `${format(weekStart, "dd MMM")} - ${format(weekEnd, "dd MMM")}`;
  };

  const getMonthDateValue = (monthLabel) => {
    const parsed = new Date(`${monthLabel} 01`);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  };

  const getWeekDateValue = (weekLabel, monthLabel) => {
    if (!weekLabel || !monthLabel) return 0;
    const year = monthLabel.split(" ").pop();
    const startLabel = weekLabel.split(" - ")[0];
    const parsed = new Date(`${startLabel} ${year}`);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  };

  const fetchEvaluations = async (month = selectedMonth, week = selectedWeek) => {
    const fetchId = ++fetchIdRef.current;
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/evaluations`, {
        params: {
          managerId: managerId || undefined,
          month: month || undefined,
          week: week || undefined,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (fetchId === fetchIdRef.current) {
        setEvaluations(Array.isArray(res.data) ? res.data : []);
      }
    } catch (err) {
      if (fetchId === fetchIdRef.current) {
        console.error(err);
        toast.error("Failed to load evaluations");
      }
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/evaluations/filter-options`, {
          params: { managerId: managerId || undefined },
          headers: { Authorization: `Bearer ${token}` },
        });

        setFilterOptions(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load evaluation filters");
      }
    };

    setSelectedMonth(getCurrentMonth());
    setSelectedWeek(getCurrentWeek());
    loadFilterOptions();
  }, []);

  useEffect(() => {
    if (!selectedMonth) return;
    fetchEvaluations(selectedMonth, selectedWeek);
  }, [selectedMonth, selectedWeek]);

  const yearOptions = useMemo(
    () =>
      [...new Set(
        filterOptions
          .map((option) => option.month?.trim())
          .filter(Boolean)
          .map((month) => month.match(/\b\d{4}\b/)?.[0])
          .filter(Boolean)
      )].sort((a, b) => Number(b) - Number(a)),
    [filterOptions]
  );

  const monthOptions = useMemo(
    () =>
      [...new Set(filterOptions.map((option) => option.month).filter(Boolean).filter(month => month.includes(selectedYear)))]
        .sort((a, b) => getMonthDateValue(b) - getMonthDateValue(a)),
    [filterOptions, selectedYear]
  );

  const weekOptions = useMemo(
    () =>
      [
        ...new Set(
          filterOptions
            .filter((option) => option.month === selectedMonth && option.week)
            .map((option) => option.week)
        ),
      ].sort((a, b) => getWeekDateValue(b, selectedMonth) - getWeekDateValue(a, selectedMonth)),
    [filterOptions, selectedMonth]
  );

  useEffect(() => {
    const currentYear = format(new Date(), "yyyy");
    if (!yearOptions.length) {
      if (selectedYear !== currentYear) {
        setSelectedYear(currentYear);
      }
      return;
    }

    if (yearOptions.includes(selectedYear)) return;

    if (yearOptions.includes(currentYear)) {
      setSelectedYear(currentYear);
      return;
    }

    setSelectedYear(yearOptions[0]);
  }, [yearOptions, selectedYear]);

  useEffect(() => {
    if (!monthOptions.length) {
      setSelectedMonth("");
      return;
    }

    if (monthOptions.includes(selectedMonth)) return;

    if (monthOptions.includes(getCurrentMonth())) {
      setSelectedMonth(getCurrentMonth());
      return;
    }

    setSelectedMonth(monthOptions[0]);
  }, [monthOptions, selectedMonth]);

  useEffect(() => {
    if (!selectedMonth) return;

    const currentWeek = getCurrentWeek();
    if (selectedWeek && weekOptions.includes(selectedWeek)) return;

    if (weekOptions.includes(currentWeek)) {
      setSelectedWeek(currentWeek);
      return;
    }

    setSelectedWeek(weekOptions[0] || "");
  }, [selectedMonth, weekOptions]);

  const [hoveredRow, setHoveredRow] = useState(null);

  const getInitials = (name = "") =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  const scoreStyle = (val, max) => {
    if (!max) return { color: "#94a3b8", fontWeight: 600 };
    const pct = val / max;
    if (pct >= 0.8) return { color: "#16a34a", fontWeight: 700 };
    if (pct >= 0.5) return { color: "#ca8a04", fontWeight: 700 };
    return { color: "#dc2626", fontWeight: 700 };
  };

  const finalScoreColor = (score) => {
    if (score >= 80) return "#16a34a";
    if (score >= 60) return "#f59e0b";
    return "#ef4444";
  };

  const positionBadge = (type) => {
    const map = {
      "Full-Time": { background: "#dbeafe", color: "#1e40af" },
      "Intern":    { background: "#dcfce7", color: "#166534" },
      "Trainee":   { background: "#fef9c3", color: "#854d0e" },
    };
    return map[type] || { background: "#f1f5f9", color: "#475569" };
  };

  const handleEdit = (evaluation) => {
    setEvaluationData({
      ...evaluation,
      ratings: {
        taskCompletion: evaluation.taskCompletion || 0,
        quality: evaluation.quality || 0,
        teamwork: evaluation.teamwork || 0,
        initiative: evaluation.initiative || 0,
        learning: evaluation.learning || 0,
      },
    });
    setShowEvaluationModal(true);
  };

  const handleSubmitEvaluation = async (submittedEvaluationData) => {
    if (!submittedEvaluationData?.id) return;

    try {
      const payload = {
        employeeId: submittedEvaluationData.employeeId,
        managerId: submittedEvaluationData.managerId,
        employeeType: submittedEvaluationData.employeeType,
        month: submittedEvaluationData.month,
        week: submittedEvaluationData.week,
        assignedTasks: Number(submittedEvaluationData.assignedTasks) || 0,
        completedTasks: Number(submittedEvaluationData.completedTasks) || 0,
        billingHours: Number(submittedEvaluationData.billingHours) || 0,
        availableHours: Number(submittedEvaluationData.availableHours) || 0,
        taskCompletion: Number(submittedEvaluationData.ratings?.taskCompletion) || 0,
        quality: Number(submittedEvaluationData.ratings?.quality) || 0,
        teamwork: Number(submittedEvaluationData.ratings?.teamwork) || 0,
        initiative: Number(submittedEvaluationData.ratings?.initiative) || 0,
        learning: Number(submittedEvaluationData.ratings?.learning) || 0,
        remarks: submittedEvaluationData.remarks || "",
      };

      await axios.put(`${API_BASE_URL}/api/evaluations/${submittedEvaluationData.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Evaluation updated successfully!");
      setShowEvaluationModal(false);
      fetchEvaluations(selectedMonth, selectedWeek);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.response?.data || "Update failed");
    }
  };

  const excellentCount = evaluations.filter((e) => e.grade === "Excellent" || e.grade?.startsWith("A")).length;
  const avgScore =
    evaluations.length
      ? Math.round(evaluations.reduce((s, e) => s + (e.finalScore ?? 0), 0) / evaluations.length)
      : 0;

  const gMap = (grade) => {
    if (!grade) return { bg: "#f1f5f9", color: "#64748b", dot: "#94a3b8" };
    if (grade === "Excellent" || grade.startsWith("A"))
      return { bg: "#dcfce7", color: "#166534", dot: "#16a34a" };
    if (grade === "Very Good" || grade.startsWith("B"))
      return { bg: "#dbeafe", color: "#1e40af", dot: "#2563eb" };
    if (grade === "Good" || grade === "C")
      return { bg: "#cffafe", color: "#155e75", dot: "#0891b2" };
    if (grade === "Average")
      return { bg: "#fef9c3", color: "#854d0e", dot: "#ca8a04" };
    return { bg: "#fee2e2", color: "#991b1b", dot: "#dc2626" };
  };

  const stickyBase = {
    position: "sticky",
    background: "#f8fafc",
    borderRight: "1px solid #e2e8f0",
    zIndex: 6,
  };

  return (
    <>
      <div className="fixed-top">
        <ManagerNavbar />
      </div>

      <div style={{ paddingTop: "100px", height: "100vh", display: "flex", flexDirection: "column", background: "#f1f5f9" }}>
        <div
          style={{
            margin: "12px 16px 16px",
            borderRadius: 16,
            overflow: "hidden",
            boxShadow: "0 4px 24px rgba(15,23,42,0.08)",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            background: "#fff",
          }}
        >
          {/* Card Header */}
          <div
            style={{
              background: "#fff",
              borderBottom: "1px solid #e2e8f0",
              padding: "14px 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: "linear-gradient(#e2e8f0)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  flexShrink: 0,
                }}
              >
                👥
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "1rem", color: "#0f172a" }}>My Team Evaluations</div>
                <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 1 }}>
                  {evaluations.length} record{evaluations.length !== 1 ? "s" : ""} &nbsp;·&nbsp;
                  <span style={{ color: "#16a34a", fontWeight: 600 }}>{excellentCount} Excellent</span>
                  &nbsp;·&nbsp; Avg Score{" "}
                  <span style={{ color: finalScoreColor(avgScore), fontWeight: 600 }}>{avgScore}</span>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {/* Month select */}
              {(() => {
                const selStyle = {
                  appearance: "none",
                  WebkitAppearance: "none",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: "6px 32px 6px 10px",
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  color: "#1e40af",
                  background: "#eff6ff url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%231e40af' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\") no-repeat right 10px center",
                  cursor: "pointer",
                  outline: "none",
                  minWidth: 160,
                };
                return (
                  <>
                    <select style={{ ...selStyle, minWidth: 90 }} value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                      {(yearOptions.length ? yearOptions : [format(new Date(), "yyyy")]).map((y, i) => (
                        <option key={i} value={y}>📅 {y}</option>
                      ))}
                    </select>
                    <select style={selStyle} value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                      {monthOptions.length ? monthOptions.map((m, i) => (
                        <option key={i} value={m}>📅 {m}</option>
                      )) : <option value="">📅 No Months</option>}
                    </select>
                    <select style={{ ...selStyle, color: "#5b21b6", background: selStyle.background.replace("#eff6ff", "#f5f3ff").replace("%231e40af", "%235b21b6"), minWidth: 190 }}
                      value={selectedWeek}
                      onChange={(e) => setSelectedWeek(e.target.value)}
                    >
                      <option value="">📆 All Weeks</option>
                      {weekOptions.map((w, i) => (
                        <option key={i} value={w}>📆 {w}</option>
                      ))}
                    </select>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Table area */}
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto" }}>
            {loading ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 14, color: "#64748b" }}>
                <div style={{ width: 40, height: 40, border: "3px solid #e2e8f0", borderTopColor: "#2563eb", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <div style={{ fontWeight: 600 }}>Loading evaluations…</div>
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
              </div>
            ) : (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  fontSize: "0.82rem",
                  whiteSpace: "nowrap",
                }}
              >
                <thead style={{ position: "sticky", top: 0, zIndex: 4 }}>
                  {/* Row 1 — group headers */}
                  <tr style={{ height: 36 }}>
                    {/* Frozen cols span 2 rows */}
                    <th rowSpan="2" style={{ ...stickyBase, left: 0, zIndex: 6, width: 48, minWidth: 48, maxWidth: 48, textAlign: "center", fontSize: "0.75rem", color: "#64748b", fontWeight: 700, verticalAlign: "middle", borderBottom: "2px solid #cbd5e1", borderTop: "1px solid #e2e8f0" }}>Sr</th>
                    <th rowSpan="2" style={{ ...stickyBase, left: 48, zIndex: 6, minWidth: 170, textAlign: "left", fontSize: "0.75rem", color: "#64748b", fontWeight: 700, verticalAlign: "middle", padding: "0 12px", borderBottom: "2px solid #cbd5e1", borderTop: "1px solid #e2e8f0" }}>Employee</th>
                    <th rowSpan="2" style={{ ...stickyBase, left: 218, zIndex: 6, minWidth: 150, textAlign: "left", fontSize: "0.75rem", color: "#64748b", fontWeight: 700, verticalAlign: "middle", padding: "0 12px", borderBottom: "2px solid #cbd5e1", borderTop: "1px solid #e2e8f0" }}>Reporter</th>
                    <th rowSpan="2" style={{ ...stickyBase, left: 368, zIndex: 6, minWidth: 100, textAlign: "center", fontSize: "0.75rem", color: "#64748b", fontWeight: 700, verticalAlign: "middle", padding: "0 12px", borderBottom: "2px solid #cbd5e1", borderTop: "1px solid #e2e8f0" }}>Position</th>
                    <th rowSpan="2" style={{ ...stickyBase, left: 468, zIndex: 6, minWidth: 120, textAlign: "center", fontSize: "0.75rem", color: "#64748b", fontWeight: 700, verticalAlign: "middle", padding: "0 12px", borderBottom: "2px solid #cbd5e1", borderTop: "1px solid #e2e8f0", boxShadow: "4px 0 8px rgba(0,0,0,0.06)" }}>Period</th>

                    {/* Performance group */}
                    <th colSpan="8" style={{ background: "#eff6ff", color: "#1d4ed8", fontWeight: 700, fontSize: "0.78rem", textAlign: "center", padding: "7px 0", borderBottom: "1px solid #bfdbfe", borderTop: "1px solid #e2e8f0", borderLeft: "1px solid #bfdbfe", borderRight: "1px solid #bfdbfe" }}>
                      ⚡ Performance Metrics
                    </th>

                    {/* Summary group */}
                    <th colSpan="4" style={{ background: "#f0fdf4", color: "#065f46", fontWeight: 700, fontSize: "0.78rem", textAlign: "center", padding: "7px 0", borderBottom: "1px solid #bbf7d0", borderTop: "1px solid #e2e8f0", borderLeft: "1px solid #bbf7d0", borderRight: "1px solid #bbf7d0" }}>
                      ✅ Summary
                    </th>
                  </tr>

                  {/* Row 2 — sub-headers */}
                  <tr style={{ height: 32 }}>
                    {/* Performance sub-headers */}
                    {[
                      ["Tasks C/A", "#dbeafe"],
                      ["Billable Hrs", "#dbeafe"],
                      ["Avail. Hrs", "#dbeafe"],
                      ["Task Cmpl.", "#dbeafe"],
                      ["Quality", "#dbeafe"],
                      ["Teamwork", "#dbeafe"],
                      ["Initiative", "#dbeafe"],
                      ["Learning", "#dbeafe"],
                    ].map(([label, bg]) => (
                      <th key={label} style={{ background: bg, color: "#1e3a8a", fontWeight: 600, fontSize: "0.72rem", textAlign: "center", padding: "6px 10px", borderBottom: "2px solid #93c5fd", borderLeft: "1px solid #bfdbfe", verticalAlign: "middle" }}>
                        {label}
                      </th>
                    ))}

                    {/* Summary sub-headers */}
                    {[
                      ["Total Score", "#bbf7d0"],
                      ["Grade", "#bbf7d0"],
                      ["Remarks", "#bbf7d0"],
                      ["Actions", "#bbf7d0"],
                    ].map(([label, bg]) => (
                      <th key={label} style={{ background: bg, color: "#065f46", fontWeight: 600, fontSize: "0.72rem", textAlign: "center", padding: "6px 10px", borderBottom: "2px solid #6ee7b7", borderLeft: "1px solid #bbf7d0", verticalAlign: "middle" }}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {evaluations.length ? (
                    evaluations.map((ev, index) => {
                      const isHovered = hoveredRow === ev.id;
                      const rowBg = isHovered ? "#f0f9ff" : index % 2 === 0 ? "#fff" : "#fafafa";
                      const stickyCell = { ...stickyBase, background: isHovered ? "#e0f2fe" : "#f8fafc" };
                      const gm = gMap(ev.grade);
                      const initials = getInitials(ev.employeeName || ev.employeeId);
                      const pb = positionBadge(ev.employeeType);

                      return (
                        <tr
                          key={ev.id}
                          onMouseEnter={() => setHoveredRow(ev.id)}
                          onMouseLeave={() => setHoveredRow(null)}
                          style={{ background: rowBg, transition: "background 0.15s" }}
                        >
                          {/* Sr */}
                          <td style={{ ...stickyCell, left: 0, textAlign: "center", padding: "10px 8px", color: "#64748b", fontWeight: 600, borderBottom: "1px solid #f1f5f9" }}>
                            {index + 1}
                          </td>

                          {/* Employee */}
                          <td style={{ ...stickyCell, left: 48, padding: "8px 12px", borderBottom: "1px solid #f1f5f9" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#2563eb,#7c3aed)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 700, flexShrink: 0 }}>
                                {initials}
                              </div>
                              <span style={{ fontWeight: 600, color: "#0f172a" }}>{ev.employeeName || ev.employeeId}</span>
                            </div>
                          </td>

                          {/* Reporter */}
                          <td style={{ ...stickyCell, left: 218, padding: "8px 12px", borderBottom: "1px solid #f1f5f9" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#f59e0b,#ef4444)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 700, flexShrink: 0 }}>
                                {getInitials(ev.managerName || String(ev.managerId || ""))}
                              </div>
                              <span style={{ fontWeight: 600, color: "#0f172a" }}>{ev.managerName || ev.managerId}</span>
                            </div>
                          </td>

                          {/* Position */}
                          <td style={{ ...stickyCell, left: 368, textAlign: "center", padding: "8px 12px", borderBottom: "1px solid #f1f5f9" }}>
                            <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, ...pb }}>
                              {ev.employeeType || "—"}
                            </span>
                          </td>

                          {/* Period */}
                          <td style={{ ...stickyCell, left: 468, textAlign: "center", padding: "8px 12px", borderBottom: "1px solid #f1f5f9", boxShadow: "4px 0 8px rgba(0,0,0,0.06)" }}>
                            <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "0.8rem" }}>{ev.month}</div>
                            <div style={{ fontSize: "0.7rem", color: "#64748b" }}>{ev.week || "—"}</div>
                          </td>

                          {/* Tasks C/A */}
                          <td style={{ textAlign: "center", padding: "10px 10px", borderBottom: "1px solid #f1f5f9", background: rowBg }}>
                            <span style={{ fontWeight: 700, color: "#1e40af" }}>{ev.completedTasks || 0}</span>
                            <span style={{ color: "#94a3b8" }}> / </span>
                            <span style={{ color: "#64748b" }}>{ev.assignedTasks || 0}</span>
                          </td>

                          {/* Billable Hrs */}
                          <td style={{ textAlign: "center", padding: "10px 10px", borderBottom: "1px solid #f1f5f9", background: rowBg, ...scoreStyle(ev.billingHours, ev.availableHours) }}>
                            {ev.billingHours || 0}
                          </td>

                          {/* Avail. Hrs */}
                          <td style={{ textAlign: "center", padding: "10px 10px", borderBottom: "1px solid #f1f5f9", background: rowBg, color: "#64748b", fontWeight: 600 }}>
                            {ev.availableHours || 0}
                          </td>

                          {/* Task Completion */}
                          <td style={{ textAlign: "center", padding: "10px 10px", borderBottom: "1px solid #f1f5f9", background: rowBg, ...scoreStyle(ev.taskCompletion, 5) }}>
                            {ev.taskCompletion || 0}
                          </td>

                          {/* Quality */}
                          <td style={{ textAlign: "center", padding: "10px 10px", borderBottom: "1px solid #f1f5f9", background: rowBg, ...scoreStyle(ev.quality, 5) }}>
                            {ev.quality || 0}
                          </td>

                          {/* Teamwork */}
                          <td style={{ textAlign: "center", padding: "10px 10px", borderBottom: "1px solid #f1f5f9", background: rowBg, ...scoreStyle(ev.teamwork, 5) }}>
                            {ev.teamwork || 0}
                          </td>

                          {/* Initiative */}
                          <td style={{ textAlign: "center", padding: "10px 10px", borderBottom: "1px solid #f1f5f9", background: rowBg, ...scoreStyle(ev.initiative, 5) }}>
                            {ev.initiative || 0}
                          </td>

                          {/* Learning */}
                          <td style={{ textAlign: "center", padding: "10px 10px", borderBottom: "1px solid #f1f5f9", background: rowBg, ...scoreStyle(ev.learning, 5) }}>
                            {ev.learning || 0}
                          </td>

                          {/* Total Score */}
                          <td style={{ textAlign: "center", padding: "10px 10px", borderBottom: "1px solid #f1f5f9", background: rowBg }}>
                            <div style={{ fontWeight: 800, fontSize: "0.92rem", color: finalScoreColor(ev.finalScore ?? 0) }}>
                              {ev.finalScore ?? 0}
                            </div>
                            <div style={{ marginTop: 4, height: 4, background: "#e2e8f0", borderRadius: 999, overflow: "hidden", minWidth: 50 }}>
                              <div style={{ height: "100%", width: `${Math.min(ev.finalScore ?? 0, 100)}%`, background: finalScoreColor(ev.finalScore ?? 0), borderRadius: 999, transition: "width 0.4s" }} />
                            </div>
                          </td>

                          {/* Grade */}
                          <td style={{ textAlign: "center", padding: "10px 10px", borderBottom: "1px solid #f1f5f9", background: rowBg }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 11px 4px 8px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, background: gm.bg, color: gm.color }}>
                              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gm.dot, flexShrink: 0 }} />
                              {ev.grade || "—"}
                            </span>
                          </td>

                          {/* Remarks */}
                          <td style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #f1f5f9", background: rowBg, maxWidth: 160 }}>
                            <span
                              title={ev.remarks}
                              style={{ display: "inline-block", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#475569", cursor: "default" }}
                            >
                              {ev.remarks || <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>—</span>}
                            </span>
                          </td>

                          {/* Actions */}
                          {(() => {
                            const isToday = ev.createdAt === format(new Date(), "yyyy-MM-dd");
                            return (
                              <td style={{ textAlign: "center", padding: "10px 10px", borderBottom: "1px solid #f1f5f9", background: rowBg }}>
                                <button
                                  title={isToday ? "View / Edit" : "Editing is only allowed on the day the evaluation was created"}
                                  onClick={isToday ? () => handleEdit(ev) : undefined}
                                  disabled={!isToday}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px",
                                    borderRadius: 7, fontWeight: 600, fontSize: "0.77rem", transition: "all 0.15s",
                                    border: isToday ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
                                    background: isToday ? "#eff6ff" : "#f8fafc",
                                    color: isToday ? "#2563eb" : "#94a3b8",
                                    cursor: isToday ? "pointer" : "not-allowed",
                                    opacity: isToday ? 1 : 0.55,
                                  }}
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z" />
                                  </svg>
                                  Edit
                                </button>
                              </td>
                            );
                          })()}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="17" style={{ padding: "60px 20px", textAlign: "center" }}>
                        <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                        <div style={{ fontWeight: 700, fontSize: "1rem", color: "#374151" }}>No evaluations found</div>
                        <div style={{ color: "#94a3b8", fontSize: "0.85rem", marginTop: 4 }}>
                          Try selecting a different month or week
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <AdminTimesheetEvaluationModal
        show={showEvaluationModal}
        selectedEmployee={null}
        manager={null}
        startDate={null}
        endDate={null}
        viewMode="monthly"
        tasks={[]}
        billingHours={0}
        availableHours={0}
        initialData={evaluationData}
        submitLabel="Update Evaluation"
        onSubmit={handleSubmitEvaluation}
        onClose={() => setShowEvaluationModal(false)}
      />
    </>
  );
};

export default ManagerEvaluation;
